import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  CreditCard, CheckCircle, Lightning, XCircle, Sparkle, Robot, PaintBrush,
  ChatCircleDots, ChartLineUp, ImageSquare, WhatsappLogo, X, Receipt, DownloadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FEATURE_ICONS = {
  analysis: ChartLineUp, agent_run: Robot, creative_job: PaintBrush,
  team_chat: ChatCircleDots, meera_chat: Sparkle, image_regen: ImageSquare, whatsapp_msg: WhatsappLogo,
};

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const featState = (f) => (f.quota < 0 ? "ok"
  : f.quota <= 0 && f.topup_balance <= 0 ? "exhausted"
  : f.used >= f.quota && f.topup_balance <= 0 ? "exhausted"
  : f.quota > 0 && f.used / f.quota >= 0.8 ? "low" : "ok");

const TopupModal = ({ feature, packs, status, user, onClose, onDone }) => {
  const [pack, setPack] = useState(packs[0]);
  const [paying, setPaying] = useState(false);
  const Icon = FEATURE_ICONS[feature.kind] || Sparkle;

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/billing/topup/order", { kind: feature.kind, qty: pack });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay checkout — check your connection"); setPaying(false); return; }
      const rz = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: "INR",
        name: "5ads.ai",
        description: `${feature.name} — ${pack} extra credits`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#FF6B35" },
        config: {
          display: {
            hide: [{ method: "upi" }],
            blocks: { upi: { name: "Pay via UPI", instruments: [{ method: "upi", flows: ["collect", "intent"] }] } },
            sequence: ["block.upi"],
            preferences: { show_default_blocks: true },
          },
        },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/billing/topup/verify", resp);
            toast.success(`+${v.qty} ${feature.name} credits added — balance: ${v.balance}`);
            onDone();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rz.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
      rz.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
      setPaying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="topup-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center"><Icon size={18} weight="duotone" /></span>
          <div>
            <div className="text-sm font-extrabold text-[#0A0D14]">Top up — {feature.name}</div>
            <div className="text-[10px] text-[#8a90a0]">₹{feature.price} per credit · instant via Razorpay</div>
          </div>
          <button onClick={onClose} data-testid="topup-close" className="ml-auto text-[#8a90a0] hover:text-[#0A0D14]"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {packs.map((p) => (
            <button key={p} onClick={() => setPack(p)} data-testid={`topup-pack-${p}`}
              className={`rounded-xl border-2 py-3 text-center transition-colors ${pack === p ? "border-[#FF6B35] bg-[#FF6B35]/5" : "border-[#E5E7EB] hover:border-[#c9cdd6]"}`}>
              <div className="text-lg font-extrabold text-[#0A0D14]">+{p}</div>
              <div className="text-[10px] font-bold text-[#8a90a0]">credits</div>
              <div className="mt-1 text-xs font-extrabold text-[#FF6B35]">₹{(p * feature.price).toLocaleString("en-IN")}</div>
            </button>
          ))}
        </div>
        <Button onClick={pay} disabled={paying || !status?.available} data-testid="topup-pay-button"
          className="mt-4 w-full rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white text-xs font-bold h-10">
          {paying ? "Opening checkout…" : `Pay ${inr((pack * feature.price))} with Razorpay`}
        </Button>
        <p className="mt-2 text-center text-[10px] text-[#8a90a0]">
          {status?.available ? "Top-up credits never expire — they're used only after your monthly quota runs out." : "Payments are not configured on this server."}
        </p>
      </div>
    </div>
  );
};

const UsageTab = ({ status, user }) => {
  const [usage, setUsage] = useState(null);
  const [topup, setTopup] = useState(null);

  const load = useCallback(() => {
    api.get("/billing/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!usage) return <div className="text-sm text-[#8a90a0]">Loading AI usage…</div>;

  const totalValue = usage.features.reduce((s, f) => s + (f.quota < 0 ? 0 : f.used * f.price), 0);
  const totalUses = usage.features.reduce((s, f) => s + f.used, 0);

  return (
    <div className="space-y-8" data-testid="billing-usage-tab">
      <div className="bg-[#0A0D14] text-white rounded-xl px-5 py-4 flex flex-wrap items-center gap-6" data-testid="usage-plan-strip">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Your plan</div>
          <div className="text-lg font-extrabold capitalize">
            {usage.plan_name}{usage.plan_price > 0 && <> — {inr(usage.plan_price / 100)}<span className="text-xs font-semibold text-white/60">/30 days (fixed)</span></>}
          </div>
        </div>
        <div className="h-8 w-px bg-white/15 hidden sm:block" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">AI usage this month</div>
          <div className="text-lg font-extrabold" data-testid="usage-total-value">{inr(totalValue)}<span className="text-xs font-semibold text-white/60"> worth · {totalUses} actions · {usage.month}</span></div>
        </div>
        <div className="ml-auto text-[11px] text-white/60 max-w-[260px]">
          Your plan includes a monthly quota of every AI feature below. Top up any feature instantly when it runs out — everything else keeps working.
        </div>
      </div>

      <div>
        <h2 className="text-sm font-extrabold text-[#0A0D14] uppercase tracking-wide">Feature quotas — <span className="capitalize">{usage.plan_name}</span> plan</h2>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {usage.features.map((f) => {
            const st = featState(f);
            const Icon = FEATURE_ICONS[f.kind] || Sparkle;
            const pct = f.quota > 0 ? Math.min(100, Math.round((f.used / f.quota) * 100)) : 100;
            return (
              <div key={f.kind} data-testid={`usage-quota-${f.kind}`}
                className={`bg-white rounded-xl border p-4 flex flex-col gap-2 ${st === "exhausted" ? "border-red-300 ring-2 ring-red-100" : st === "low" ? "border-amber-300" : "border-[#E5E7EB]"}`}>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center shrink-0"><Icon size={16} weight="duotone" /></span>
                  <span className="text-sm font-extrabold text-[#0A0D14]">{f.name}</span>
                  {st === "exhausted" && <span className="ml-auto rounded-full bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 text-[9px] font-bold uppercase shrink-0">Exhausted</span>}
                  {st === "low" && <span className="ml-auto rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[9px] font-bold uppercase shrink-0">Running low</span>}
                </div>
                <p className="text-[11px] text-[#8a90a0] leading-snug">{f.desc}</p>
                {f.quota < 0 ? (
                  <div className="mt-auto">
                    <div className="flex items-center justify-between text-[11px] font-bold text-[#525866]">
                      <span>{f.used} sent this month</span>
                      <span className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[9px] font-bold uppercase">Unlimited</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-emerald-100 overflow-hidden">
                      <div className="h-full w-full rounded-full bg-gradient-to-r from-emerald-300 to-emerald-500 opacity-60" />
                    </div>
                    <div className="mt-3 h-8 flex items-center justify-center text-[11px] font-bold text-[#8a90a0]">
                      Included free on every plan
                    </div>
                  </div>
                ) : (
                <div className="mt-auto">
                  <div className="flex justify-between text-[11px] font-bold text-[#525866]">
                    <span>{f.used > f.quota ? <>{f.used} used · {f.quota} included</> : <>{f.used} / {f.quota} used</>}{f.topup_balance > 0 && <span className="text-[#00953e]"> · +{f.topup_balance} top-up left</span>}</span>
                    <span>₹{f.price}/extra</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div className={`h-full rounded-full ${st === "exhausted" ? "bg-red-500" : st === "low" ? "bg-amber-400" : "bg-[#00953e]"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <Button onClick={() => setTopup(f)} data-testid={`usage-topup-${f.kind}`}
                    variant={st === "exhausted" ? "default" : "outline"}
                    className={`mt-3 w-full rounded-full text-xs font-bold h-8 ${st === "exhausted"
                      ? "bg-[#FF6B35] hover:bg-[#E85A25] text-white"
                      : "border-[#E5E7EB] text-[#525866] hover:border-[#c9cdd6] bg-white"}`}>
                    {st === "exhausted" ? <><Lightning size={13} weight="fill" className="mr-1" /> Top up now</> : "Buy extra credits"}
                  </Button>
                </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-extrabold text-[#0A0D14] uppercase tracking-wide">AI service price list</h2>
        <div className="mt-3 bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto" data-testid="usage-price-list">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#FAFAFB] text-left text-[10px] uppercase tracking-wider text-[#8a90a0]">
                <th className="px-4 py-3 font-bold">AI service</th>
                <th className="px-4 py-3 font-bold">What one credit does</th>
                <th className="px-4 py-3 font-bold">Included / month</th>
                <th className="px-4 py-3 font-bold">Price per extra</th>
              </tr>
            </thead>
            <tbody>
              {usage.features.map((f) => (
                <tr key={f.kind} className="border-b border-[#F3F4F6] last:border-0">
                  <td className="px-4 py-3 text-xs font-extrabold text-[#0A0D14] whitespace-nowrap">{f.name}</td>
                  <td className="px-4 py-3 text-[11px] text-[#525866] max-w-[300px]">{f.desc}</td>
                  <td className="px-4 py-3 text-xs font-bold text-[#0A0D14]">{f.quota < 0 ? "Unlimited" : `${f.quota}/month`}</td>
                  <td className="px-4 py-3 text-xs font-extrabold text-[#FF6B35]">{f.quota < 0 ? "—" : `₹${f.price}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-extrabold text-[#0A0D14] uppercase tracking-wide">This month's usage</h2>
        <div className="mt-3 bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto" data-testid="usage-breakdown">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#FAFAFB] text-left text-[10px] uppercase tracking-wider text-[#8a90a0]">
                <th className="px-4 py-3 font-bold">Service</th>
                <th className="px-4 py-3 font-bold">Uses</th>
                <th className="px-4 py-3 font-bold">Value used</th>
                <th className="px-4 py-3 font-bold">From top-ups</th>
              </tr>
            </thead>
            <tbody>
              {usage.features.map((f) => (
                <tr key={f.kind} className="border-b border-[#F3F4F6] last:border-0">
                  <td className="px-4 py-3 text-xs font-bold text-[#0A0D14]">{f.name}</td>
                  <td className="px-4 py-3 text-xs">{f.used}</td>
                  <td className="px-4 py-3 text-xs">{f.quota < 0 ? <span className="text-emerald-600 font-bold">Included free</span> : inr(f.used * f.price)}</td>
                  <td className="px-4 py-3 text-xs text-[#8a90a0]">{f.used_topup > 0 ? `${f.used_topup} used` : "—"}</td>
                </tr>
              ))}
              <tr className="bg-[#FAFAFB]">
                <td className="px-4 py-3 text-xs font-extrabold text-[#0A0D14]">Total</td>
                <td className="px-4 py-3 text-xs font-extrabold">{totalUses}</td>
                <td className="px-4 py-3 text-xs font-extrabold">{inr(totalValue)}</td>
                <td className="px-4 py-3 text-xs font-extrabold">{usage.features.reduce((s, f) => s + f.used_topup, 0) || "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {topup && (
        <TopupModal feature={topup} packs={usage.packs} status={status} user={user}
          onClose={() => setTopup(null)} onDone={() => { setTopup(null); load(); }} />
      )}
    </div>
  );
};

const InvoicesTab = () => {
  const [invoices, setInvoices] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    api.get("/billing/invoices").then(({ data }) => setInvoices(data.invoices)).catch(() => setInvoices([]));
  }, []);

  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      const { data } = await api.get(`/billing/invoices/${inv.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `5ads-receipt-${inv.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the receipt — try again");
    } finally {
      setDownloading("");
    }
  };

  if (!invoices) return <div className="text-sm text-[#8a90a0]">Loading invoices…</div>;

  if (invoices.length === 0)
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-10 text-center" data-testid="invoices-empty">
        <Receipt size={32} weight="duotone" className="text-[#c9cdd6] mx-auto" />
        <p className="mt-3 text-sm font-bold text-[#0A0D14]">No payments yet</p>
        <p className="mt-1 text-xs text-[#8a90a0]">Plan purchases and AI credit top-ups will appear here with downloadable receipts.</p>
      </div>
    );

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto" data-testid="invoices-table">
      <table className="w-full text-sm min-w-[680px]">
        <thead>
          <tr className="border-b border-[#E5E7EB] bg-[#FAFAFB] text-left text-[10px] uppercase tracking-wider text-[#8a90a0]">
            <th className="px-4 py-3 font-bold">Date</th>
            <th className="px-4 py-3 font-bold">Description</th>
            <th className="px-4 py-3 font-bold">Type</th>
            <th className="px-4 py-3 font-bold">Payment ID</th>
            <th className="px-4 py-3 font-bold text-right">Amount</th>
            <th className="px-4 py-3 font-bold text-right">Receipt</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id} className="border-b border-[#F3F4F6] last:border-0" data-testid={`invoice-row-${inv.id}`}>
              <td className="px-4 py-3 text-xs text-[#525866] whitespace-nowrap">{inv.date ? new Date(inv.date).toLocaleDateString() : "—"}</td>
              <td className="px-4 py-3 text-xs font-bold text-[#0A0D14]">{inv.description}</td>
              <td className="px-4 py-3">
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase border ${inv.type === "plan"
                  ? "bg-[#FF6B35]/10 text-[#c2410c] border-[#FF6B35]/25"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                  {inv.type === "plan" ? "Plan" : "Top-up"}
                </span>
              </td>
              <td className="px-4 py-3 text-[11px] text-[#8a90a0] font-mono whitespace-nowrap">{inv.payment_id || "—"}</td>
              <td className="px-4 py-3 text-xs font-extrabold text-[#0A0D14] text-right whitespace-nowrap">{inr(inv.amount)}</td>
              <td className="px-4 py-3 text-right">
                <button onClick={() => download(inv)} disabled={downloading === inv.id} data-testid={`invoice-download-${inv.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] hover:border-[#FF6B35] hover:text-[#FF6B35] px-3 py-1.5 text-[11px] font-bold text-[#525866] transition-colors">
                  <DownloadSimple size={13} weight="bold" /> {downloading === inv.id ? "Preparing…" : "PDF"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Billing() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["plan", "usage", "invoices"].includes(t) ? t : "plan";
  });

  const load = useCallback(() => {
    api.get("/billing/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (planKey) => {
    setBusy(planKey);
    try {
      const { data } = await api.post("/billing/subscribe", { plan: planKey });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay checkout — check your connection"); setBusy(""); return; }
      const opts = {
        key: data.key_id,
        name: "5ads.ai",
        description: `${planKey === "pro" ? "Pro" : "Agency"} plan — 30 days`,
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#FF6B35" },
        config: {
          display: {
            hide: [{ method: "upi" }],
            blocks: {
              upi: { name: "Pay via UPI", instruments: [{ method: "upi", flows: ["collect", "intent"] }] },
            },
            sequence: ["block.upi"],
            preferences: { show_default_blocks: true },
          },
        },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/billing/verify", resp);
            toast.success(v.mode === "order"
              ? `Payment verified — ${v.plan} plan active until ${new Date(v.expires_at).toLocaleDateString()}!`
              : "Payment verified — plan upgraded!");
            load();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
        },
        modal: { ondismiss: () => setBusy("") },
      };
      if (data.mode === "order") {
        opts.order_id = data.order_id;
        opts.amount = data.amount;
        opts.currency = "INR";
      } else {
        opts.subscription_id = data.subscription_id;
      }
      const rz = new window.Razorpay(opts);
      rz.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
      rz.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
    } finally {
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription cancelled — you're back on the Free plan");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!status) return <div className="text-sm text-[#8a90a0]">Loading billing…</div>;

  const { plan, plans, usage, subscription, available } = status;

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex gap-1 bg-white border border-[#E5E7EB] rounded-lg p-1 w-fit text-xs font-bold" data-testid="billing-tabs">
        <button onClick={() => setTab("plan")} data-testid="billing-tab-plan"
          className={`px-4 py-2 rounded-md transition-colors ${tab === "plan" ? "bg-[#0A0D14] text-white" : "text-[#8a90a0] hover:text-[#0A0D14]"}`}>
          Plan &amp; Payments
        </button>
        <button onClick={() => setTab("usage")} data-testid="billing-tab-usage"
          className={`px-4 py-2 rounded-md transition-colors ${tab === "usage" ? "bg-[#0A0D14] text-white" : "text-[#8a90a0] hover:text-[#0A0D14]"}`}>
          AI Usage &amp; Charges
        </button>
        <button onClick={() => setTab("invoices")} data-testid="billing-tab-invoices"
          className={`px-4 py-2 rounded-md transition-colors ${tab === "invoices" ? "bg-[#0A0D14] text-white" : "text-[#8a90a0] hover:text-[#0A0D14]"}`}>
          Invoices
        </button>
      </div>

      {tab === "usage" ? (
        <UsageTab status={status} user={user} />
      ) : tab === "invoices" ? (
        <InvoicesTab />
      ) : (
        <div className="space-y-8">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="billing-current-card">
            <div className="flex flex-wrap items-center gap-3">
              <span className="h-10 w-10 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center">
                <CreditCard size={20} weight="duotone" className="text-[#FF6B35]" />
              </span>
              <div>
                <div className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">
                  Current plan: <span className="capitalize" data-testid="billing-current-plan">{plan}</span>
                </div>
                <p className="text-xs text-[#8a90a0]">Every plan includes monthly quotas of each AI feature — see the AI Usage &amp; Charges tab</p>
              </div>
              {subscription && (
                <Badge className="ml-auto bg-success/10 text-[#00953e] border border-success/25 rounded-full font-bold text-[10px] uppercase">
                  {subscription.mode === "order" ? "active · 30-day" : subscription.status}
                </Badge>
              )}
            </div>
            {status.billing_state?.status === "grace" ? (
              <p className="mt-2 text-xs font-bold text-red-600" data-testid="billing-expiry">
                Plan expired on {new Date(status.plan_expires_at).toLocaleDateString()} — workspace is read-only. Renew within{" "}
                {status.billing_state.days_left} day{status.billing_state.days_left === 1 ? "" : "s"} or your account will be locked.
              </p>
            ) : status.billing_state?.status === "locked" ? (
              <p className="mt-2 text-xs font-bold text-red-600" data-testid="billing-expiry">
                Workspace locked — your plan expired and the 15-day renewal window has passed. Renew below to restore access.
              </p>
            ) : status.plan_expires_at ? (
              <p className="mt-2 text-xs text-[#525866]" data-testid="billing-expiry">
                Plan valid until <span className="font-bold">{new Date(status.plan_expires_at).toLocaleDateString()}</span> — renewing early adds 30 days on top of your current expiry.
              </p>
            ) : null}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold text-[#525866]" data-testid="billing-usage-text">
                {usage.used} AI action{usage.used === 1 ? "" : "s"} used in {usage.month}
              </span>
              <button onClick={() => setTab("usage")} data-testid="billing-view-usage"
                className="text-xs font-bold text-[#FF6B35] hover:underline">
                View per-feature usage &amp; top-ups →
              </button>
            </div>
            {subscription && subscription.mode !== "order" && (
              <Button onClick={cancel} disabled={busy === "cancel"} variant="outline" data-testid="billing-cancel-button"
                className="mt-5 rounded-full border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 font-bold text-xs">
                <XCircle size={15} className="mr-1.5" /> {busy === "cancel" ? "Cancelling…" : "Cancel subscription"}
              </Button>
            )}
          </div>

          {!available && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-xs text-[#8a6400] leading-relaxed" data-testid="billing-unavailable-note">
              Payments are not configured — set RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET in the backend .env to enable checkout.
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6" data-testid="billing-plans-grid">
            {plans.map((p) => {
              const current = p.key === plan;
              const highlight = p.key === "pro";
              return (
                <div key={p.key} data-testid={`billing-plan-${p.key}`}
                  className={`rounded-xl border bg-white p-6 flex flex-col ${highlight ? "border-[#FF6B35] shadow-[0_8px_30px_rgba(255,107,53,0.12)]" : "border-[#E5E7EB]"}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">{p.name}</h3>
                    {highlight && <Badge className="bg-[#FF6B35] text-white rounded-full font-bold text-[10px]">POPULAR</Badge>}
                    {current && <Badge className="bg-[#0A0D14] text-white rounded-full font-bold text-[10px]">CURRENT</Badge>}
                  </div>
                  <div className="mt-3 font-heading text-3xl font-extrabold tracking-tighter text-[#0A0D14]">
                    {p.price === 0 ? "₹0" : inr(p.price / 100)}
                    <span className="text-sm font-semibold text-[#8a90a0]">{p.price === 0 ? "" : "/30 days"}</span>
                  </div>
                  <p className="text-xs text-[#8a90a0] mt-1">{p.blurb}</p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-[#525866]">
                        <CheckCircle size={16} weight="fill" className="text-[#FF6B35] shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  {p.price > 0 && (!current || status.plan_expires_at) ? (
                    <Button onClick={() => subscribe(p.key)} disabled={!available || !!busy} data-testid={`billing-subscribe-${p.key}`}
                      className={`mt-6 w-full rounded-full font-bold text-sm h-11 transition-transform hover:-translate-y-[1px] active:scale-[0.98] ${
                        highlight ? "bg-[#FF6B35] hover:bg-[#E85A25] text-white" : "bg-[#0A0D14] hover:bg-[#1c2230] text-white"}`}>
                      <Lightning size={15} weight="fill" className="mr-1.5" />
                      {busy === p.key ? "Opening checkout…" : current ? "Renew 30 days" : `Upgrade to ${p.name}`}
                    </Button>
                  ) : (
                    <div className="mt-6 h-11 flex items-center justify-center text-xs font-bold text-[#8a90a0]">
                      {current ? "Your current plan" : "Included free"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-lg border border-[#E5E7EB] bg-white px-5 py-4 text-xs text-[#8a90a0] leading-relaxed flex items-start gap-2">
            <Sparkle size={15} weight="duotone" className="text-[#FF6B35] shrink-0 mt-0.5" />
            <span>
              One-time 30-day payments — no auto-renewal. Renew early to stack 30 days on top of your expiry; after expiry you get a 15-day read-only window before the workspace locks. Razorpay convenience fee may apply at checkout.
              {status.test_mode && " Test mode: card 4111 1111 1111 1111, any future expiry, any CVV, OTP 1234."}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
